/**
 * The base reference of a curated global — drawing it, and accepting it.
 *
 * This is the **admin-pre-render** lean made concrete (PRD 06). A global primitive's render
 * belongs to no author, so nobody in the Studio can ask for it: `resolve` from a parent's
 * browser stamps `requestedBy`, and a global render must not carry one. So the picture the
 * shared shelf shows is drawn here, by us, on our money — which is why the cost is stated in
 * dollars rather than in a family-facing euphemism.
 *
 * A **character** is established from a model sheet, and approving it here does two things:
 * it locks the identity for everyone who forks the piece, and it fires the free `sharp` split
 * that cuts the sheet into starter variants. That is what makes a starter arrive with poses
 * rather than one flat drawing.
 *
 * Until this panel has been used, Browse says *not drawn yet* and its **Use as is** is
 * disabled — there is nothing to place. After approval, both come alive.
 */

import { Button, CostLabel, Dialog, FramedWindow, StatusBadge } from '@atlas-nook/design-system';
import { DEFAULT_THEME_ID } from '@atlas-nook/brand';
import { priceOf } from '@atlas-nook/image-worker/cost';
import { establishmentModeFor, variantInstanceId } from '@atlas-nook/story';
import type { Primitive, VariantInstance } from '@atlas-nook/story';
import { useState } from 'react';
import type { JSX } from 'react';

import { requestRetryCall } from '../data/callables.js';
import { useVariantInstance } from '../data/queries.js';
import { approveGlobalBaseReference, preRender } from '../data/writes.js';
import { useSession } from '../auth/AuthProvider.js';
import { useToast } from '../shell/ToastProvider.js';
import { ADMIN_STRINGS as S } from '../strings.js';
import { RenderImage } from './RenderImage.js';

type Ask = 'draw' | 'redraw' | 'approve';

/** The five states a base reference can be in, named the way this app talks about them. */
export type ReferenceState = 'none' | 'making' | 'needs-ok' | 'ready' | 'error';

export function referenceStateOf(
  primitive: Pick<Primitive, 'kind'>,
  base: VariantInstance | null,
): ReferenceState {
  if (base === null) return 'none';
  switch (base.status) {
    case 'pending':
    case 'generating':
      return 'making';
    case 'error':
      return 'error';
    case 'ready':
      // Only a character carries the human gate; a setting or a prop is usable once ready.
      if (primitive.kind !== 'character') return 'ready';
      return base.approved === true ? 'ready' : 'needs-ok';
  }
}

export function PreRenderPanel({ primitive }: { primitive: Primitive }): JSX.Element {
  const session = useSession();
  const toast = useToast();
  const [ask, setAsk] = useState<Ask | null>(null);
  const [working, setWorking] = useState(false);

  const baseId = variantInstanceId(primitive.id, primitive.version, {}, DEFAULT_THEME_ID);
  const { data: base } = useVariantInstance(baseId);
  const state = referenceStateOf(primitive, base);
  const wantsSheet = establishmentModeFor(primitive) === 'model-sheet';

  async function draw(force: boolean): Promise<void> {
    setWorking(true);
    try {
      await preRender({
        primitive,
        variant: {},
        themeId: DEFAULT_THEME_ID,
        ...(force ? { force: true } : {}),
      });
      setAsk(null);
    } catch {
      toast.show(S.common.failed);
    } finally {
      setWorking(false);
    }
  }

  async function approve(): Promise<void> {
    if (base === null) return;
    setWorking(true);
    try {
      await approveGlobalBaseReference(base, session.uid);
      setAsk(null);
      toast.show(S.global.approved);
    } catch {
      toast.show(S.common.failed);
    } finally {
      setWorking(false);
    }
  }

  async function retry(): Promise<void> {
    const result = await requestRetryCall(baseId);
    if (!result.ok) toast.show(S.global.retryFailed);
  }

  const badge =
    state === 'ready'
      ? {
          status: 'ready' as const,
          label: primitive.kind === 'character' ? S.global.approvedTag : S.global.readyTag,
        }
      : state === 'needs-ok'
        ? { status: 'needs-ok' as const, label: S.global.awaitingApproval }
        : state === 'making'
          ? { status: 'generating' as const, label: S.global.making }
          : state === 'error'
            ? { status: 'error' as const, label: S.global.failed }
            : { status: 'none' as const, label: S.globals.notDrawn };

  return (
    <div className="prerender">
      <div className="stack" style={{ maxWidth: 360 }}>
        <FramedWindow
          caption={wantsSheet ? S.global.sheet : S.global.reference}
          aspectRatio="1 / 1"
          state={
            state === 'ready' || state === 'needs-ok'
              ? 'filled'
              : state === 'making'
                ? 'making'
                : 'empty'
          }
          locked={state === 'ready' && primitive.kind === 'character'}
        >
          {base?.assetPath === undefined ? (
            <span className="cap">{S.global.referenceNone}</span>
          ) : (
            <RenderImage path={base.assetPath} alt={primitive.name.en} />
          )}
        </FramedWindow>
      </div>

      <div className="stack">
        <StatusBadge status={badge.status}>{badge.label}</StatusBadge>

        {wantsSheet && <p className="note">{S.global.sheetHelp}</p>}

        {base?.sheetSplit !== undefined && (
          <p className="cap">
            {S.global.sheetSplit(base.sheetSplit.usable, base.sheetSplit.expected)}
            {base.sheetSplit.remakeRecommended === true ? ` · ${S.global.remakeRecommended}` : ''}
          </p>
        )}

        {base?.error !== undefined && <p className="cap">{base.error}</p>}

        <div className="btnrow">
          {state === 'none' && (
            <Button tone="primary" onClick={() => setAsk('draw')}>
              {S.global.draw}
            </Button>
          )}
          {state === 'needs-ok' && (
            <Button tone="primary" onClick={() => setAsk('approve')}>
              {S.global.approve}
            </Button>
          )}
          {state === 'error' && (
            <Button tone="secondary" onClick={() => void retry()}>
              {S.global.retry}
            </Button>
          )}
          {(state === 'ready' || state === 'needs-ok' || state === 'error') && (
            <Button tone="quiet" onClick={() => setAsk('redraw')}>
              {S.global.drawAgain}
            </Button>
          )}
        </div>

        <CostLabel emphasis={`$${priceOf('1K').toFixed(4)}`}>{S.cost.ours} </CostLabel>
      </div>

      <Dialog
        open={ask === 'draw' || ask === 'redraw'}
        onClose={() => setAsk(null)}
        title={
          ask === 'redraw'
            ? S.global.redrawTitle(primitive.name.en)
            : S.global.drawTitle(primitive.name.en)
        }
        cost={S.cost.oneGeneration(`$${priceOf('1K').toFixed(4)}`)}
        actions={
          <>
            <Button tone="quiet" onClick={() => setAsk(null)}>
              {S.common.cancel}
            </Button>
            <Button tone="primary" working={working} onClick={() => void draw(ask === 'redraw')}>
              {ask === 'redraw' ? S.global.redrawConfirm : S.global.drawConfirm}
            </Button>
          </>
        }
      >
        <p className="note">{ask === 'redraw' ? S.global.redrawBody : S.global.drawBody}</p>
      </Dialog>

      <Dialog
        open={ask === 'approve'}
        onClose={() => setAsk(null)}
        title={S.global.approveTitle(primitive.name.en)}
        actions={
          <>
            <Button tone="quiet" onClick={() => setAsk(null)}>
              {S.common.cancel}
            </Button>
            <Button tone="primary" working={working} onClick={() => void approve()}>
              {S.global.approveConfirm}
            </Button>
          </>
        }
      >
        <p className="note">{S.global.approveBody}</p>
      </Dialog>
    </div>
  );
}
